import { useEffect, useCallback, useRef, useState, startTransition } from 'react'
import { useTagManager } from '@/hooks/analytics/useTagManager'
import { useUser } from '@/hooks/users/useUser'
import { IProfile } from '@/types'
import { useSentry } from '@/hooks/utils/useSentry'

import settings from '@/settings/public'

/**
 * User data passed to the onIdentified callback
 */
export type UserIdentifiedData = {
  /** Whether the user is currently logged in */
  isLoggedIn: boolean
  /** Intercom hash for user identification (only present if withIntercom is enabled) */
  intercomHash?: string
}

/**
 * Options for configuring the useUserIdentified hook behavior
 */
export type UseUserIdentifiedOptions = {
  /**
   * Callback function to be called when the user is identified.
   * This is triggered once per session when identification completes.
   * @param userData - Object containing login status and optional Intercom hash
   */
  onIdentified?: (userData: UserIdentifiedData) => void
  /**
   * Whether to fetch and use Intercom hash for user identification.
   * When enabled, the hook will fetch the Intercom hash from the API
   * and include it in the callback and return value.
   * @default false
   */
  withIntercom?: boolean
}

/**
 * Return value of the useUserIdentified hook
 */
export type UseUserIdentifiedReturn = {
  /**
   * Whether the user has been identified in this session.
   * This becomes true after identification completes (analytics events sent, etc.)
   */
  isIdentified: boolean
  /**
   * Whether user data is currently being loaded.
   * True while checking authentication status and fetching user profile.
   */
  isLoading: boolean
  /**
   * Whether a session has been established (user data has finished loading).
   * This is the inverse of isLoading - true when we know the user's auth state.
   */
  hasSession: boolean
  /**
   * Whether the current session is for a logged-in user.
   * True when hasSession is true AND the user is authenticated.
   */
  isUserSession: boolean
  /**
   * Whether the current session is for an anonymous (not logged-in) user.
   * True when hasSession is true AND the user is not authenticated.
   */
  isAnonymousSession: boolean
  /**
   * Whether the user is currently logged in.
   * This reflects the authentication state from useUser hook.
   */
  isLoggedIn: boolean
  /**
   * Intercom hash for user identification.
   * Only populated if withIntercom option is enabled and user is logged in.
   * Undefined for anonymous users or when withIntercom is false.
   */
  intercomHash: string | undefined
  /**
   * User profile data from useUser hook.
   * Contains user information when logged in, undefined for anonymous users.
   */
  profile: IProfile | undefined
}

/** The server's expected rejection for a caller without a real user session. */
const UNAUTHORIZED_STATUS = 401

/**
 * Fetches the Intercom hash for the current user from the API.
 * This hash is used to securely identify users in Intercom chat.
 * The server derives the user's identity from the session cookie;
 * unauthenticated or Guest callers gracefully receive an empty hash.
 *
 * A 401 is an expected outcome (no user session server-side) and degrades
 * quietly; any other non-2xx is an infrastructure fault surfaced via
 * `faultStatus` so the caller can report it — otherwise an upstream outage
 * silently disables Intercom identity with zero signal.
 *
 * @returns Promise resolving to the hash, plus `faultStatus` on infra faults
 */
const fetchIntercomHash = async (): Promise<{ hash: string, faultStatus?: number }> => {
  const url = `${settings.api.host}/api/generic/intercom`
  const response = await fetch(url)
  if (response.ok) return response.json()
  if (response.status === UNAUTHORIZED_STATUS) return { hash: '' }

  return { hash: '', faultStatus: response.status }
}

/**
 * Hook to handle user identification for analytics and related features.
 *
 * This hook manages the user identification lifecycle, including:
 * - Sending analytics events when a user is identified
 * - Fetching Intercom hash for logged-in users (if enabled)
 * - Tracking identification state to prevent duplicate events
 * - Resetting identification state when users log out and log back in
 *
 * The hook automatically identifies users once per session:
 * - For logged-in users: Sends analytics events and optionally fetches Intercom hash
 * - For anonymous users: Marks as identified immediately without analytics
 *
 * @param options - Configuration options for the hook
 * @param options.onIdentified - Optional callback invoked when identification completes
 * @param options.withIntercom - Whether to fetch Intercom hash (default: false)
 *
 * @returns Object containing identification state, session information, and user data
 *
 * @example
 * ```tsx
 * const { isIdentified, isUserSession, profile } = useUserIdentified({
 *   withIntercom: true,
 *   onIdentified: (data) => {
 *     console.log('User identified:', data.isLoggedIn, data.intercomHash)
 *   }
 * })
 *
 * if (!isIdentified) {
 *   return <LoadingSpinner />
 * }
 * ```
 */
export const useUserIdentified = (options: UseUserIdentifiedOptions = {}): UseUserIdentifiedReturn => {
  // ---------------
  // 1. Configuration and Options
  // ---------------
  /** Destructured options with defaults */
  const { onIdentified, withIntercom = false } = options

  // ---------------
  // 2. State and Hooks
  // ---------------
  /** Tag Manager instance for sending analytics events */
  const { sendGtm, clearGtmUserData, pushGtmUserIdentity } = useTagManager()
  /** Sentry user context setter + handled-error reporter */
  const { setUser, captureError } = useSentry()
  /** User authentication state and profile data */
  const { isLoggedIn, profile, isLoading: isLoadingUser } = useUser()
  /** Intercom hash for user identification (only set if withIntercom is enabled) */
  const [intercomHash, setIntercomHash] = useState<string | undefined>()
  /** Ref tracking whether identification has completed in this session */
  const hasBeenIdentified = useRef(false)
  /** State tracking whether identification has completed (for UI updates) */
  const [isIdentifiedState, setIsIdentifiedState] = useState(false)
  /** Ref tracking previous login state to detect logout events */
  const prevIsLoggedIn = useRef(isLoggedIn)
  /** Generation counter incremented on each auth state reset, used to invalidate stale async handleIdentify calls */
  const resetGeneration = useRef(0)

  // ---------------
  // 3. Core Identification Logic
  // ---------------
  /**
   * Handles the user identification process.
   * This function:
   * 1. Fetches Intercom hash if enabled
   * 2. Sends analytics events (USER_SESSION_IDENTIFIED)
   * 3. Updates identification state
   * 4. Invokes the onIdentified callback
   *
   * This function is idempotent - it will only run once per session
   * (checked via hasBeenIdentified ref).
   */
  const handleIdentify = useCallback(async () => {
    if (hasBeenIdentified.current) return

    const myGeneration = resetGeneration.current
    // Latch synchronously before any await so a concurrent re-invocation (e.g. a
    // new profile reference re-running the effect during the Intercom fetch) bails
    // out — USER_SESSION_IDENTIFIED must fire exactly once per identification cycle.
    hasBeenIdentified.current = true

    try {
      let hash = ''
      if (withIntercom) {
        try {
          const response = await fetchIntercomHash()
          hash = response?.hash || ''
          if (response?.faultStatus) {
            // Swallowed to degrade gracefully — report it or the outage is invisible.
            captureError(new Error(`Intercom hash fetch failed: ${response.faultStatus}`), {}, 'warning')
          }
        } catch (err) {
          captureError(err, {}, 'warning')
        }
      }

      // Bail out if a reset occurred while we were awaiting — a new identification
      // cycle will handle it. The hash write sits AFTER this check: writing first
      // would push the previous user's hash back into state the reset just cleared.
      if (resetGeneration.current !== myGeneration) return
      if (withIntercom) setIntercomHash(hash)

      if (profile) {
        setUser({
          id: profile._id,
          email: profile.email,
          country: profile.country?.code,
        })
      }
      // Always emit the key when Intercom is on (empty string on failure): GTM's
      // dataLayer retains omitted keys from the previous push, so omitting here
      // would boot a later user with the prior user's verification hash.
      sendGtm.USER_SESSION_IDENTIFIED({ user: profile, ...(withIntercom ? { intercomHash: hash } : {}) })

      onIdentified?.({ isLoggedIn, intercomHash: hash })
    } finally {
      // Only publish identified state if no reset happened during this async flow.
      // On a generation mismatch the reset effect already cleared the latch — leave
      // it untouched so the fresh cycle can run (do not roll back here).
      if (resetGeneration.current === myGeneration) {
        setIsIdentifiedState(true)
      }
    }
  }, [sendGtm, withIntercom, isLoggedIn, onIdentified, profile, setUser, captureError])

  // ---------------
  // 4. Additional Logic (Add new features here)
  // ---------------
  // Example: You can add more business logic in this section
  // Such as user preference handling, feature enablement, etc.

  // ---------------
  // 5. Side Effects
  // ---------------
  /**
   * Effect: Reset identification state when login state changes.
   *
   * Monitors login state changes and resets all identification-related state
   * when:
   * - User logs out (was logged in, now not logged in)
   * - Anonymous user logs in (was not logged in, now logged in)
   *
   * This ensures that when a user's auth state changes, the identification
   * process will run again with the correct user context.
   *
   * Resets:
   * - hasBeenIdentified ref (allows re-identification)
   * - isIdentifiedState (updates UI)
   * - intercomHash (clears previous user's hash)
   */
  useEffect(() => {
    if (prevIsLoggedIn.current !== isLoggedIn) {
      // On logout, also null the GTM dataLayer identity: clearing React state
      // alone leaves the last push's user/intercomHash in GTM's data model for
      // the whole anonymous session (the anonymous branch never pushes).
      if (prevIsLoggedIn.current && !isLoggedIn) clearGtmUserData()
      // Backstop for the reverse transition: a logged-in load whose SSR
      // carried no identity (profile prefetch failed, or the session
      // degraded to the anonymous fallback) and where no auth event fires
      // because the user did not just log in — pair-code validation, for
      // instance. A real login already pushes identity synchronously from
      // useAuth.onAuthenticated; USER_SESSION_IDENTIFIED below is too late,
      // it waits on the awaited Intercom hash fetch.
      if (!prevIsLoggedIn.current && isLoggedIn && profile?._id) pushGtmUserIdentity(profile._id)
      resetGeneration.current++
      hasBeenIdentified.current = false
      setIsIdentifiedState(false)
      setIntercomHash(undefined)
    }
    prevIsLoggedIn.current = isLoggedIn
  }, [isLoggedIn, clearGtmUserData, pushGtmUserIdentity, profile?._id])

  /**
   * Effect: Perform user identification when ready.
   *
   * This effect handles the main identification logic:
   * - Waits for user data to finish loading
   * - For logged-in users: Calls handleIdentify() which sends analytics and fetches Intercom hash
   * - For anonymous users: Marks as identified immediately without analytics
   *
   * The identification only runs once per session (enforced by hasBeenIdentified ref).
   */
  useEffect(() => {
    if (hasBeenIdentified.current) return

    // Wait for user data to load before identifying
    if (isLoadingUser) return

    if (isLoggedIn) {
      // Logged-in user: full identification with analytics and Intercom
      startTransition(() => { void handleIdentify() })
    } else {
      // Anonymous user: mark as identified immediately without analytics
      hasBeenIdentified.current = true
      startTransition(() => setIsIdentifiedState(true))
      onIdentified?.({ isLoggedIn: false })
    }
  }, [isLoadingUser, isLoggedIn, handleIdentify, onIdentified])

  // ---------------
  // 6. Return Values
  // ---------------
  return {
    isIdentified: isIdentifiedState,
    isLoading: isLoadingUser,
    hasSession: !isLoadingUser,
    isUserSession: !isLoadingUser && isLoggedIn,
    isAnonymousSession: !isLoadingUser && !isLoggedIn,
    isLoggedIn,
    intercomHash,
    profile,
    // Add additional return values here as needed
  }
}
